import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Button, 
  Card, 
  CardContent, 
  CardMedia, 
  IconButton, 
  useTheme as useMuiTheme 
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import GitHubIcon from '@mui/icons-material/GitHub';
import LaunchIcon from '@mui/icons-material/Launch';

// Styled components
const ProjectCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
  borderRadius: '15px',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.card,
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const ProjectImageWrapper = styled(CardMedia)(({ theme }) => ({
  position: 'relative',
  width: '40%',
  height: 'auto',
  overflow: 'hidden',
  '&:hover': {
    '& > div': {
      opacity: 1,
    },
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    height: '200px',
  },
}));

const ProjectImage = styled(CardMedia)(({ theme }) => ({
  height: '100%',
  width: '100%',
  position: 'absolute',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const CardOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  opacity: 0,
  transition: 'opacity 0.3s ease',
  padding: theme.spacing(3),
  color: '#fff',
  textAlign: 'center',
  '&:hover': {
    opacity: 1,
  },
}));

const LinkIconButton = styled(IconButton)(({ theme }) => ({
  color: '#fff',
  backgroundColor: theme.palette.primary.main,
  margin: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'scale(1.1)',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(6),
  position: 'relative',
  display: 'inline-block',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '80px',
    height: '4px',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '2px',
  },
}));

const ViewMoreButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(6),
  padding: '12px 30px',
  borderRadius: '30px',
  backgroundColor: 'transparent',
  border: `2px solid ${theme.palette.primary.main}`,
  color: theme.palette.text.primary,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)',
  },
}));

const Portfolio = () => {
  const theme = useMuiTheme();

  const projects = [
    {
      id: 'cyclesafe',
      title: 'CycleSafe',
      description: 'Real-time IoT system for road-hazard detection and cyclist alerts using ESP32 edge sensing and smartphone GPS gateway. Built a 1D-CNN pipeline on IMU and ultrasonic data achieving ~65-70% precision. Features synchronized sensing with MQTT publishing for live hazard mapping with 1-2 second end-to-end latency.',
      image: process.env.PUBLIC_URL + '/Images/CycleSafe.png',
      github: 'https://github.com/Mahesh1772/CycleSafe',
      live: null
    },
    {
      id: 'itinerary-app',
      title: 'Itinerary App',
      description: 'TravelMate is a mobile application that helps users plan their trips by providing a platform to create and share itineraries. The app is built using Flutter and Dart, and is designed to be user-friendly and intuitive.',
      image: process.env.PUBLIC_URL + '/Images/Itineraray poster.png',
      github: 'https://github.com/Mahesh1772/ItineraryApp',
      live: null
    },
    {
      id: 'soldier-tracking',
      title: 'Soldier Tracking App',
      description: 'TroopTrak is a mobile application that streamlines and simplifies administrative tasks of NS commanders. It offers a centralized storage and real-time CRUD functionalites, built using Flutter, Dart and Google Firestore.',
      image: process.env.PUBLIC_URL + '/Images/TroopTrak (1).png',
      github: 'https://github.com/Mahesh1772/Orbital',
      live: null
    },
    {
      id: 'mapping-automaton',
      title: 'Mapping Automaton',
      description: 'Alex is a Robot built using Raspberry Pi as the core, with its peripherals controlled using Arduino Uno. It has a 2D LiDAR, which is used to map its surrounding using ROS packages. It simulates a search-and-rescue bot, which can be controlled remotely from the map data retrieved.',
      image: process.env.PUBLIC_URL +'/Images/Alex.jpg',
      github: 'https://github.com/Mahesh1772/CG2111A-Alex',
      live: null
    },
    {
      id: 'rtos-project',
      title: 'RTOS Project',
      description: 'An ARM Cortex-M0 microcontroller system using bare-metal C/C++ and RTOS (RTX 5). Utilizes RTOS kernel and synchronization primitives to create multi-threaded applications. Wirelessly controlled via Wi-Fi with an ESP32 module interfaced through UART.',
      image: process.env.PUBLIC_URL + '/Images/RTOS.jpg',
      github: 'https://github.com/Mahesh1772/RTOS_Project',
      live: null
    },
    {
      id: 'coin-rush',
      title: 'Coin Rush',
      description: 'A 2D video game developed on a Xilinx Artix-7 FPGA using Verilog HDL. Game contains 3 enemy types, 5 different collectibles, 2D physics simulation (acceleration, momentum, collision detection), game kinematics, and dynamic difficulty scaling which render on a VGA monitor.',
      image: process.env.PUBLIC_URL + '/Images/Coin_Rush.jpg',
      github: 'https://github.com/Mahesh1772/Coin_Rush',
      live: null
    },
    {
      id: 'cv-vlm-asr-nlp',
      title: 'CV/VLM and ASR/NLP',
      description: 'Models designed for detecting targets within images and extracting instructions from voice commands. The CV/VLM model takes an image and a text string as input, outputting bounding boxes of the target object. The ASR/NLP model processes a voice command and outputs a JSON file containing direction, target, and ammunition information.',
      image: process.env.PUBLIC_URL + '/Images/SortaBin.jpg',
      github: 'https://github.com/Mahesh1772/CV_VLM_ASR_NLP_Task',
      live: null
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    }
  };

  return (
    <Box 
      id="portfolio" 
      sx={{ 
        py: { xs: 8, md: 12 },
        backgroundColor: 'background.default',
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <SectionTitle
              variant="h2"
              component="h2"
              sx={{ 
                fontWeight: 700,
                color: 'primary.main'
              }}
            >
              My Portfolio
            </SectionTitle>
          </motion.div>
        </Box>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Grid container spacing={4}>
            {projects.map((project) => (
              <Grid item xs={12} sm={6} key={project.id}>
                <motion.div variants={cardVariants}>
                  <ProjectCard>
                    <ProjectImageWrapper>
                      <ProjectImage
                        image={project.image}
                        title={project.title}
                      />
                      <CardOverlay>
                        <Typography variant="h6" gutterBottom>
                          {project.title}
                        </Typography>
                        <Box>
                          <LinkIconButton 
                            href={project.github} 
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="GitHub repository"
                          >
                            <GitHubIcon />
                          </LinkIconButton>
                          {project.live && (
                            <LinkIconButton 
                              href={project.live} 
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Live demo"
                            >
                              <LaunchIcon />
                            </LinkIconButton>
                          )}
                        </Box>
                      </CardOverlay>
                    </ProjectImageWrapper>
                    <CardContent sx={{ 
                      flexGrow: 1, 
                      p: 3, 
                      width: { xs: '100%', sm: '60%' } 
                    }}>
                      <Typography 
                        variant="h5" 
                        component="h3" 
                        sx={{ 
                          fontWeight: 700,
                          mb: 2,
                          fontSize: { xs: '1.25rem', md: '1.5rem' }
                        }}
                      >
                        {project.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          mb: 2,
                          lineHeight: 1.6,
                          display: '-webkit-box',
                          overflow: 'hidden',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: { xs: 4, sm: 6 },
                        }}
                      >
                        {project.description}
                      </Typography>
                    </CardContent>
                  </ProjectCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        <Box sx={{ textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <ViewMoreButton
              href="https://github.com/Mahesh1772"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<GitHubIcon />}
            >
              See More on GitHub
            </ViewMoreButton>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default Portfolio; 